/**
 * Immutable time value (hours, minutes, seconds) for use with TimeInput and APIs.
 * Display format: "HH:MM" (colon); standard string format: "HH:mm:ss" (ISO-style).
 */
export class Time {
  readonly hours: number;
  readonly minutes: number;
  readonly seconds: number;

  constructor(hours: number, minutes: number, seconds = 0) {
    this.hours = Math.max(0, Math.min(23, Math.floor(hours)));
    this.minutes = Math.max(0, Math.min(59, Math.floor(minutes)));
    this.seconds = Math.max(0, Math.min(59, Math.floor(seconds)));
  }

  /** Returns true if hours/minutes/seconds are in valid ranges */
  isValid(): boolean {
    return (
      this.hours >= 0 &&
      this.hours <= 23 &&
      this.minutes >= 0 &&
      this.minutes <= 59 &&
      this.seconds >= 0 &&
      this.seconds <= 59
    );
  }

  /** Input display format: "HH:MM" (e.g. "14:30") */
  toDisplayString(): string {
    const h = String(this.hours).padStart(2, '0');
    const m = String(this.minutes).padStart(2, '0');
    return `${h}:${m}`;
  }

  /** Standard time string: "HH:mm:ss" (e.g. "14:30:00") */
  toTimeString(): string {
    const h = String(this.hours).padStart(2, '0');
    const m = String(this.minutes).padStart(2, '0');
    const s = String(this.seconds).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  /** Short time string: "HH:mm" (e.g. "14:30") */
  toShortTimeString(): string {
    const h = String(this.hours).padStart(2, '0');
    const m = String(this.minutes).padStart(2, '0');
    return `${h}:${m}`;
  }

  /** Returns true if value is a valid time string. HH.MM (dot) or HH:mm (colon), 24h. */
  static isValidDisplayString(value: string): boolean {
    if (!value || value.length < 5) return false;
    return /^([01]\d|2[0-3])[.:]([0-5]\d)$/.test(value.trim());
  }

  /** Format raw input to "HH:MM" display pattern (digits only, max 4, colon after 2nd). */
  static formatInput(value: string): string {
    const digits = value.replace(/\D/g, '');
    let formatted = '';
    for (let i = 0; i < digits.length && i < 4; i++) {
      formatted += digits[i];
      if (i === 1) formatted += ':';
    }
    return formatted;
  }

  /** Parse "HH.MM" or "HH:mm" string. Returns null if invalid. */
  static fromDisplayString(value: string): Time | null {
    if (!Time.isValidDisplayString(value)) return null;
    const parts = value.split(/[.:]/);
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    return new Time(hours, minutes, 0);
  }

  /** Parse "HH:mm" or "HH:mm:ss" standard string (colon separator). Returns null if invalid. */
  static fromTimeString(value: string): Time | null {
    if (!value || value.length < 5) return null;
    const parts = value.split(':');
    if (parts.length < 2 || parts.length > 3) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    const seconds = parts.length === 3 ? parseInt(parts[2], 10) : 0;
    if (Number.isNaN(hours) || Number.isNaN(minutes) || Number.isNaN(seconds)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59 || seconds < 0 || seconds > 59) {
      return null;
    }
    return new Time(hours, minutes, seconds);
  }

  equals(other: Time): boolean {
    return (
      this.hours === other.hours && this.minutes === other.minutes && this.seconds === other.seconds
    );
  }
}
